import type { RefObject } from 'react';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';

// ─── Constants ──────────────────────────────────────────────────────────────

const DEFAULT_CHAT_WIDTH = 425;

// ─── Hook: chat panel resize ────────────────────────────────────────────────

interface UseChatPanelResizeOptions {
  defaultWidth?: number;
  /** Which side of the layout the panel is on. Affects drag direction. Default: 'right' */
  side?: 'left' | 'right';
}

interface UseChatPanelResizeResult {
  chatWidth: number;
  rootRef: RefObject<HTMLDivElement | null>;
  handleResizeMouseDown: (e: React.MouseEvent) => void;
}

export function useChatPanelResize(options?: UseChatPanelResizeOptions): UseChatPanelResizeResult {
  const defaultWidth = options?.defaultWidth ?? DEFAULT_CHAT_WIDTH;
  const side = options?.side ?? 'right';
  const [chatWidth, setChatWidth] = useState(defaultWidth);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleWindowResize = (): void => {
      const parentWidth = rootRef.current?.clientWidth ?? 0;
      const maxWidth = Math.max(defaultWidth, Math.floor(parentWidth * 0.5));
      setChatWidth((prev) => Math.min(prev, maxWidth));
    };
    window.addEventListener('resize', handleWindowResize);
    return () => window.removeEventListener('resize', handleWindowResize);
  }, [defaultWidth]);

  const handleResizeMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      const startX = e.clientX;
      const startWidth = chatWidth;
      const parentWidth = rootRef.current?.clientWidth ?? 0;
      const maxWidth = Math.max(defaultWidth, Math.floor(parentWidth * 0.5));

      const handleMouseMove = (moveEvent: MouseEvent): void => {
        const delta = side === 'right'
          ? startX - moveEvent.clientX
          : moveEvent.clientX - startX;
        setChatWidth(Math.min(maxWidth, Math.max(defaultWidth, startWidth + delta)));
      };

      const handleMouseUp = (): void => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    },
    [chatWidth, defaultWidth, side]
  );

  return { chatWidth, rootRef, handleResizeMouseDown };
}

// ─── Shared Styled Components ───────────────────────────────────────────────

export const ResizeHandle = styled.div({
  width: 12,
  flexShrink: 0,
  cursor: 'col-resize',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '&::after': {
    content: '""',
    width: 3,
    height: 48,
    borderRadius: 2,
    background: 'rgba(0, 0, 0, 0.1)',
    transition: 'background 0.15s',
  },
  '&:hover::after, &:active::after': {
    background: 'rgba(86, 90, 221, 0.5)',
  },
});

export const ChatRightModule = styled.div<{ $chatWidth: number; $expanded?: boolean }>((p) => ({
  ...(p.$expanded ? { flex: 1 } : { width: p.$chatWidth }),
  height: '100%',
  flexShrink: 0,
  display: 'flex',
  flexDirection: 'column',
  background: '#ffffff',
  borderRadius: 12,
  overflow: 'hidden',
  boxShadow: '0 2px 6px 2px rgba(60, 64, 67, 0.15)',
}));

export const SidebarArea = styled.div({
  flex: 1,
  padding: 6,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
});

export const ChatToggleBtn = styled.button({
  position: 'fixed',
  bottom: 32,
  /* Center in the gutter between the 1280px LeftModule right edge and the viewport right edge.
     Root has 12px padding, so available width = 100vw - 24px, gutter = (available - 1280) / 2.
     Midpoint of right gutter from viewport right = 12px + gutter / 2, minus half the
     button width (28px) since 'right' positions the element's right edge. */
  right: 'max(24px, calc(12px + (100vw - 1304px) / 4 - 28px))',
  width: 56,
  height: 56,
  borderRadius: '50%',
  background: '#565add',
  color: '#ffffff',
  border: 'none',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxShadow: '0 4px 16px rgba(86, 90, 221, 0.4)',
  transition: 'all 0.2s',
  zIndex: 20,
  '&:hover': {
    background: '#3035d5',
    transform: 'scale(1.05)',
  },
});

export const LightbulbIcon = memo(({ size = 24 }: { size?: number }) => (
  <svg width={size} height={size * 1.453125} viewBox="0 0 64 93">
    <g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
      <g transform="translate(0.000000, 0.532895)" fill="#FFFFFF">
        <path d="M31.7889491,0 C49.582517,0 64.0070539,14.4245369 64.0070539,32.2181048 C64.0070539,43.229438 55.3430402,53.8556946 53.0045763,56.7359851 C48.713087,62.021821 46.9510888,69 46.9510888,69 L46.9510888,69 L17.0423385,69 C17.0423385,69 14.7442364,62.2580428 12.6958226,59.2636249 C7.98169936,52.3724124 1.79056769e-12,43.9589005 1.79056769e-12,32.2181048 C1.79056769e-12,14.4245369 13.9953811,0 31.7889491,0 Z M27.6677686,10.5387571 C20.765059,11.0267154 10,18.2511509 10,30.8811507 C10,31.9877446 10.4360932,33.6152192 12.991116,33.6152192 C14.8554286,33.6152192 16.0071662,32.0507432 15.9768013,30.1890769 C15.9402464,27.9479051 16.4348334,24.2233839 20.2165138,20.3223271 C23.6541843,16.7761397 27.3252788,16.6266879 29.5726115,16.4247808 C31.7862728,16.2258989 32.9883926,15.8858499 32.9883926,13.335709 C32.9883926,10.5387571 31.0947998,10.2964974 27.6677686,10.5387571 Z" />
        <path d="M32.5,91.8671049 C40.5081289,91.8671049 47,84.0768595 47,74.4671049 C47,74.4671049 18,74.4671049 18,74.4671049 C18,84.0768595 24.4918711,91.8671049 32.5,91.8671049 Z" />
      </g>
    </g>
  </svg>
));
